import React, { useEffect, useState, useContext } from "react";
import MovementSchedule from "../../../components/Schedules/Others/MovementSchedule";
import { initSchedule } from "../../../utils/data/initData";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { PL_SCHEDULES } from "../../../utils/data/controlData";

const MovementSchedulePage = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditPLSchedule } = useAccessControl(PL_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Movement",
      scheduleTitle: schedule.name,
      openingBalance: scheduleDetails.openingBalance,
      closingBalance: scheduleDetails.closingBalance,
      movementRows: scheduleDetails.movementRows,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
      totalAdjustment: scheduleDetails.totalAdjustment,
      doc: scheduleDetails.doc,
    };

    setLoading(true);
    const response = await updatePLSchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        return;
      }
      //API call here
      const response = await getScheduleData(scheduleId, 'Movement');

      const scheduleData = response.schedule;
      if (scheduleData) {
        setSchedule(initSchedule(scheduleData, "Provision Movement"));
        setScheduleDetails({
          movementLines: scheduleData.movementRows
            .filter((item) => !item.status || item.status !== "autoload")
            .map((item) => ({
              name: item.description || item.name,
              amount: item.amount.toString(),
              taxAdjustment: item.taxAdjustment ? item.taxAdjustment : "",
              reference: item.reference,
              balance: "",
              source: item.source,
              permitToDelete: true,
              status: item?.status || "",
            })),
          sourceLines: scheduleData.movementRows.filter(
            (item) => item.status && item.status === "autoload"
          ),
          openingBalance: scheduleData.openingBalance,
        });
      }

      setLoading(false);
    };

    fetchData();
  }, [saveTrigger, scheduleId, company.uen, getScheduleData, updatePLSchedule]);

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <MovementSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditPLSchedule}
        />
      )}
    </>
  );
};

export default MovementSchedulePage;
